/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { endOfDay } from "date-fns";

import { Access, PermissionsEnum } from "common";
import { useDefaultDateFormat } from "helpers";
import { Breadcrumbs, DatePicker, Form, Loading, Page } from "components";

import vouchers from "services/vouchers";
import api from "services/api";

const breadcrumbs = [
  { name: "Home", to: "/" },
  {
    name: "Campanhas de Cashback Externo",
    to: "/external-cashback-campaigns/list",
  },
  { name: "Nova campanha", to: "" },
];

export function Create() {
  const [saving, setSaving] = useState(false);

  const [title, setTitle] = useState("");
  const [cashbackValue, setCashbackValue] = useState(0);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [points, setPoints] = useState(0);
  const [activatedOnlyOnce, setActivatedOnlyOnce] = useState(false);
  const [activatedAutomatically, setActivatedAutomatically] = useState(false);
  const [activationLimit, setActivationLimit] = useState(1);
  const [appOnly, setAppOnly] = useState(false);
  const [active, setActive] = useState(false);

  const [allChannels, setAllChannels] = useState(true);
  const [allPlans, setAllPlans] = useState(true);

  const [planList, setPlanList] = useState([]);
  const [channelList, setChannelList] = useState([]);

  const getPlans = () => {
    api
      .get("experience/v2/plans", {
        params: {
          page: 1,
          per: 99999,
        },
      })
      .then(({ data }) => {
        setPlanList(
          data.data.map(({ id, attributes }) => ({
            value: id,
            label: attributes.name,
          }))
        );
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getChannels = () => {
    vouchers
      .get("/channels", {
        params: {
          page: 1,
          per: 99999,
        },
      })
      .then(({ data }) => {
        setChannelList(
          data.data.map(({ id, attributes }) => ({
            value: id,
            label: attributes.name,
          }))
        );
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setSaving(true);

    if (!title || !startDate || !endDate) {
      setSaving(false);
      return Swal.fire({
        title: "Campos obrigatórios",
        text: "Todos os campos precisam ser preenchidos",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }

    if(cashbackValue <= 0) {
      setSaving(false);
      return Swal.fire({
        title: "Campos obrigatórios",
        text: "O valor do cashback deve ser maior que 0",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }

    if (!startDate || !endDate) {
      setSaving(false);
      return Swal.fire({
        title: "Campos obrigatórios",
        text: "Precisa informar o início e o fim da campanha!",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }

    if (new Date(startDate) > new Date(endDate)) {
      setSaving(false);
      return Swal.fire({
        title: "Campos obrigatórios",
        text: "A data de inicio deve ser menor que a de fim!",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }

    if (!allPlans && (!selectedPlans || selectedPlans.length === 0)) {
      setSaving(false);
      return Swal.fire({
        title: "Informe os planos",
        text: "Você precisa informar os planos que deseja incluir nesta campanha!",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }

    if (!allChannels && (!selectedChannels || selectedChannels.length === 0)) {
      setSaving(false);
      return Swal.fire({
        title: "Informe os canais",
        text: "Você precisa informar os canais que deseja incluir nesta campanha!",
        icon: "warning",
        confirmButtonText: "Ok",
      });
    }

    const payload = {
      title,
      points: Number(points),
      cashback: Number(cashbackValue),
      start_at: useDefaultDateFormat(startDate) ?? null,
      end_at: useDefaultDateFormat(endOfDay(endDate)) ?? null,
      active,
      app_only: appOnly,
      activation_limit: Number(activationLimit),
      activated_only_once: activatedOnlyOnce,
      activated_automatically: activatedAutomatically,
      channel_ids: allChannels
        ? channelList.map(({ value }) => value)
        : selectedChannels.map(({ value }) => value),
      plan_ids: allPlans
        ? planList.map(({ value }) => value)
        : selectedPlans.map(({ value }) => value),
    };

    vouchers
      .post("/external/campaigns", payload)
      .then(() => {
        setSaving(false);
        Swal.fire({
          title: "Campanha criada com sucesso!",
          icon: "success",
          confirmButtonText: "Ok",
        }).then(() => {
          window.location = "/#/external-cashback-campaigns/list";
        });
      })
      .catch((error) => {
        console.log(error);
        setSaving(false);
        Swal.fire({
          title: "Erro ao criar campanha!",
          text: "Ocorreu um erro ao criar esta campanha. Tente novamente.",
          icon: "error",
          confirmButtonText: "Ok",
        });
      });
  };

  useEffect(() => {
    getPlans();
    getChannels();
  }, []);

  return (
    <>
      <Page>
        <Breadcrumbs breadcrumbs={breadcrumbs} />

        <Form className="page-form" onSubmit={handleSubmit}>
          <h3 className="form-title">Criação de Campanha Externa</h3>
          <Form.Field>
            <Form.Label>Título</Form.Label>
            <Form.Input
              name="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Digite o título da campanha"
            />
          </Form.Field>

          <Form.Field>
            <Form.Label>Valor do Cashback</Form.Label>
            <Form.Input
              name="cashback_value"
              value={cashbackValue}
              type="number"
              onChange={(e) => setCashbackValue(e.target.value)}
              placeholder="Digite o valor do cashback"
            />
          </Form.Field>

          <Form.Field>
            <Form.Label>Limite de ativações</Form.Label>
            <Form.Input
              name="activation_limit"
              value={activationLimit}
              type="number"
              onChange={(e) => setActivationLimit(e.target.value)}
              placeholder="Digite o limite de ativações"
            />
          </Form.Field>

          <Form.Field>
            <Form.Label>Pontos</Form.Label>
            <Form.Input
              name="points"
              value={points}
              type="number"
              onChange={(e) => setPoints(e.target.value)}
              placeholder="Digite os pontos"
            />
          </Form.Field>

          <Form.Field className="datepicker-field">
            <Form.Label>Início</Form.Label>
            <DatePicker
              locale="pt-BR"
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              dateFormat="dd/MM/yyyy"
              minDate={new Date()}
            />
            <div className="icon-input">
              <span className="glyph glyph-calendar" />
            </div>
          </Form.Field>

          <Form.Field className="datepicker-field">
            <Form.Label>Fim</Form.Label>
            <DatePicker
              locale="pt-BR"
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              minDate={startDate}
              disabled={!startDate}
              dateFormat="dd/MM/yyyy"
            />
            <div className="icon-input">
              <span className="glyph glyph-calendar" />
            </div>
          </Form.Field>

          <Form.Field className="field-select">
            <Form.Label>Canais</Form.Label>
            <Form.Select
              name="channels"
              value={selectedChannels}
              options={channelList}
              isMulti
              disabled={allChannels}
              clearable
              searchable
              closeOnSelect={false}
              placeholder={
                allChannels ? "Todos os canais" : "Selecione os canais"
              }
              onChange={(value) => setSelectedChannels(value)}
            />
          </Form.Field>

          <Form.Field className="checkbox-field">
            <Form.Label>Todos os canais</Form.Label>
            <Form.Checkbox
              checked={allChannels}
              name="all_channels"
              onChange={(event) => setAllChannels(event)}
              label="Todos os canais"
            />
          </Form.Field>

          <Form.Field className="field-select">
            <Form.Label>Planos</Form.Label>
            <Form.Select
              name="plans"
              value={selectedPlans}
              options={planList}
              isMulti
              disabled={allPlans}
              clearable
              searchable
              closeOnSelect={false}
              placeholder={allPlans ? "Todos os planos" : "Selecione os planos"}
              onChange={(value) => setSelectedPlans(value)}
            />
          </Form.Field>

          <Form.Field className="checkbox-field">
            <Form.Label>Todos os planos</Form.Label>
            <Form.Checkbox
              checked={allPlans}
              name="all_plans"
              onChange={(event) => setAllPlans(event)}
              label="Todos os planos"
            />
          </Form.Field>

          <hr />

          <Form.Field className="checkbox-field">
            <Form.Label>Ativado apenas uma vez</Form.Label>
            <Form.Checkbox
              name="activated_only_once"
              checked={activatedOnlyOnce}
              onChange={(event) => setActivatedOnlyOnce(event)}
              label="Somente uma vez"
            />
          </Form.Field>

          <Form.Field className="checkbox-field">
            <Form.Label>Ativa automaticamente</Form.Label>
            <Form.Checkbox
              checked={activatedAutomatically}
              name="activated_automatically"
              onChange={(event) => setActivatedAutomatically(event)}
              label="Ativa automaticamente"
            />
          </Form.Field>

          <Form.Field className="checkbox-field">
            <Form.Label>Somente aplicativo</Form.Label>
            <Form.Checkbox
              checked={appOnly}
              name="app_only"
              onChange={(event) => setAppOnly(event)}
              label="Somente aplicativo"
            />
          </Form.Field>

          <Form.Field className="checkbox-field">
            <Form.Label>Ativo</Form.Label>
            <Form.Checkbox
              checked={active}
              name="active"
              onChange={(event) => setActive(event)}
              label="Ativo"
            />
          </Form.Field>

          <div className="buttons">
            <Link
              to="/external-cashback-campaigns/list"
              className="button-cancel"
            >
              Cancelar
            </Link>
            {Access.hasPermissions(
              PermissionsEnum.EDIT_EXTERNAL_CASHBACK_CAMPAIGN
            ) && (
              <Form.Button className="button-save" disabled={saving}>
                {saving ? (
                  <Loading type="btn" size="small" text="Salvando..." />
                ) : (
                  "Salvar"
                )}
              </Form.Button>
            )}
          </div>
        </Form>
      </Page>
    </>
  );
}
